import { PlayerGender, PlayerProfile, TeamProfile } from "../types/tournament";

export const sanitizePlayerGender = (value: unknown): PlayerGender => {
  if (value === "male" || value === "female") return value;
  return "unspecified";
};

const createPlayerProfile = (
  fallbackTeamId: string,
  playerIndex: number,
  source?: PlayerProfile
): PlayerProfile => {
  const fallbackId = `${fallbackTeamId}-player-${playerIndex + 1}`;
  return {
    id: source?.id?.toString().trim() ?? "",
    name: source?.name ?? "",
    alias: source?.alias ?? source?.name ?? "",
    gender: sanitizePlayerGender(source?.gender),
    isExpat: Boolean(source?.isExpat),
    startingLevel: source?.startingLevel ?? "Unspecified",
    currentElo: typeof source?.currentElo === "number" ? source.currentElo : null,
  };
};

export const ensurePlayerSlotCount = (
  fallbackTeamId: string,
  players: PlayerProfile[] | undefined,
  desiredCount: number
): PlayerProfile[] => {
  const existing = Array.isArray(players) ? players : [];
  const normalized = existing.map((player, index) => createPlayerProfile(fallbackTeamId, index, player));
  if (normalized.length < desiredCount) {
    for (let index = normalized.length; index < desiredCount; index++) {
      normalized.push(createPlayerProfile(fallbackTeamId, index));
    }
  } else if (desiredCount > 0) {
    normalized.length = desiredCount;
  }
  return normalized;
};

const compareIdStrings = (a: string, b: string) => {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }
  return a.localeCompare(b);
};

export const sortPlayerIds = (ids: string[]): string[] => {
  return [...ids].sort(compareIdStrings);
};

export const deriveTeamId = (
  divisionId: string,
  teamIndex: number,
  players: PlayerProfile[]
) => {
  const ids = players
    .map((player) => player.id?.toString().trim())
    .filter((id): id is string => Boolean(id));

  if (ids.length >= 2) {
    const sorted = sortPlayerIds(ids);
    return `team-${sorted.join("-")}`;
  }

  if (ids.length === 1) {
    return `team-${ids[0]}`;
  }

  return `${divisionId}-team-${teamIndex + 1}`;
};

export const buildPairName = (
  players: PlayerProfile[],
  teamIndex: number,
  fallbackName?: string
) => {
  const aliases = players.map((player) => player.alias?.trim()).filter(Boolean) as string[];
  if (aliases.length >= 2) {
    return `${aliases[0]} / ${aliases[1]}`;
  }
  if (aliases.length === 1) {
    return `${aliases[0]} / TBD`;
  }

  const names = players.map((player) => player.name?.trim()).filter(Boolean) as string[];
  if (names.length >= 2) {
    return `${names[0]} / ${names[1]}`;
  }
  if (names.length === 1) {
    return `${names[0]} / TBD`;
  }

  if (fallbackName && fallbackName.trim()) {
    return fallbackName.trim();
  }

  return `Pair ${teamIndex + 1}`;
};

export const finalizeTeamProfile = (
  divisionId: string,
  teamIndex: number,
  profile: Partial<TeamProfile> | null | undefined
): TeamProfile => {
  const fallbackTeamId = profile?.teamId?.trim() || `${divisionId}-team-${teamIndex + 1}`;
  const players = ensurePlayerSlotCount(fallbackTeamId, profile?.players, 2);
  const teamId = deriveTeamId(divisionId, teamIndex, players);
  const name = buildPairName(players, teamIndex, profile?.name);

  return {
    teamId,
    name,
    players,
  };
};

export const ensureTeamProfiles = (
  divisionId: string,
  pairs: string[],
  source?: TeamProfile[] | null
): TeamProfile[] => {
  const base = Array.isArray(source) ? source : [];
  return pairs.map((pairName, index) =>
    finalizeTeamProfile(divisionId, index, base[index] ?? { name: pairName, teamId: "", players: [] })
  );
};
